/**
 * Thread Service
 *
 * Handles all thread and message operations via the backend API.
 * Uses apiClient for consistent request handling and response unwrapping.
 */

import apiClient, { ApiError, dispatchUsageSignal, dispatchBillingError, apiErrorParts } from './apiClient';
import authService from './authService';
import config from '../config';

class ThreadService {
  /**
   * Create a new thread
   * @param {string} title - Thread title
   * @returns {Promise<object>} Created thread
   */
  async createThread(title, metadata = null) {
    return apiClient.post('/api/threads', { title, ...(metadata && { metadata }) });
  }

  /**
   * Find existing builder thread for a role, or return null
   * @param {string} rolId - Role ID
   * @returns {Promise<{thread: object|null}>}
   */
  async findBuilderThread(rolId) {
    return apiClient.get(`/api/threads/builder/${rolId}`);
  }

  /**
   * Find the last conversation thread for an agent/role
   * @param {string} rolId - Role ID
   * @returns {Promise<{thread: object|null}>}
   */
  async findLastAgentThread(rolId) {
    return apiClient.get(`/api/threads/last-agent/${rolId}`);
  }

  /**
   * Get a single thread by id, regardless of whether it's in the currently
   * loaded (paginated) sidebar thread list. Throws ApiError (404) if the
   * thread doesn't exist or isn't owned by the current user.
   * @param {string} threadId
   * @returns {Promise<object>} Thread
   */
  async getThread(threadId) {
    return apiClient.get(`/api/threads/${threadId}`);
  }

  /**
   * Find existing onboarding thread for current user, or return null
   * @returns {Promise<{thread: object|null}>}
   */
  async findOnboardingThread() {
    return apiClient.get('/api/threads/onboarding');
  }

  /**
   * Create a new onboarding thread
   * @param {string} title - Thread title
   * @returns {Promise<object>} Created thread with metadata
   */
  async createOnboardingThread(title = 'Onboarding') {
    return this.createThread(title, { type: 'onboarding' });
  }

  /**
   * Create a new builder thread for a role
   * @param {string} rolId - Role ID
   * @param {string} title - Thread title
   * @returns {Promise<object>} Created thread with metadata
   */
  async createBuilderThread(rolId, title = 'Agent Builder') {
    return this.createThread(title, { type: 'role-builder', rolId });
  }

  /**
   * Create the PREVIEW thread for a role (#890).
   *
   * Its own endpoint on purpose: the backend marks the thread with
   * `chat_thread.preview_rol_id`, and that column is what makes the engine run the DRAFT
   * (`draft_*`) instead of the published config. It is not part of `metadata` because metadata
   * is client-written and unvalidated — see the note in messageService.createPreviewThread.
   * @param {string} rolId - Role ID whose draft will be tested
   * @param {string} title - Thread title
   * @returns {Promise<object>} Created thread (includes previewRolId)
   */
  async createPreviewThread(rolId, title = 'Preview') {
    return apiClient.post('/api/threads/preview', { rolId, title });
  }

  /**
   * Create a new thread for plan creation.
   * Sets metadata.type='plan' so the backend forces rol='Planner', bypassing
   * the Broker LLM classifier and routing directly to the Planner workflow.
   * @returns {Promise<object>} Created thread
   */
  async createPlanThread() {
    return this.createThread('Plan de trabajo', { type: 'plan' });
  }

  /**
   * Create a new message in a thread
   * @param {string} threadId - Thread ID
   * @param {string} content - Message content
   * @param {File[]} files - Array of files to attach (optional)
   * @returns {Promise<object>} Created message with structure:
   *   {
   *     id: string,
   *     content: string,
   *     sender: 'usuario' | 'assistant',
   *     agentName?: string,
   *     color?: string,
   *     messageType: string,
   *     threadId: string,
   *     userId: string,
   *     timestamp: string,
   *     updatedAt: string,
   *     attachments?: [{
   *       id: string,
   *       fileName: string,
   *       fileType: string,
   *       fileSize: number,
   *       fileUrl: string,
   *       thumbnailUrl?: string,
   *       uploadedAt: string
   *     }]
   *   }
   */
  async createMessage(threadId, content, files = [], filesMetadata = []) {
    let body;

    if (files && files.length > 0) {
      // Use FormData for file uploads
      body = new FormData();
      body.append('content', content);
      files.forEach(file => body.append('files', file));

      // Send client-generated thumbnails for persistence (PDF, TXT, MD previews)
      // Backend will decode base64, upload to S3, and save thumbnail_url
      if (filesMetadata && filesMetadata.length > 0) {
        const thumbnailsData = filesMetadata.map(meta => ({
          fileName: meta.fileName,
          thumbnailDataUrl: meta.thumbnailDataUrl
        }));
        // 🔍 THUMBNAIL DEBUG: Log what goes into FormData
        console.log('[threadService] 🔍 THUMBNAIL DEBUG - clientThumbnails payload:', thumbnailsData.map(t => ({
          fileName: t.fileName,
          hasThumbnail: !!t.thumbnailDataUrl,
          thumbnailLength: t.thumbnailDataUrl?.length || 0,
          thumbnailPrefix: t.thumbnailDataUrl?.substring(0, 50) || 'NULL'
        })));
        body.append('clientThumbnails', JSON.stringify(thumbnailsData));
      } else {
        console.log('[threadService] 🔍 THUMBNAIL DEBUG - No filesMetadata, skipping clientThumbnails');
      }
    } else {
      // Use JSON for text-only messages
      body = { content };
    }

    return apiClient.post(`/api/threads/${threadId}/messages`, body);
  }

  /**
   * Create a new message with file attachments and upload progress tracking.
   *
   * Uses XMLHttpRequest under the hood (instead of fetch) so we can listen to
   * `xhr.upload.onprogress` and surface real transfer progress to the UI.
   *
   * Mirrors the response contract of `createMessage` (returns unwrapped `data`
   * and throws `ApiError` on failure) so callers can swap implementations.
   *
   * @param {string} threadId
   * @param {string} content
   * @param {File[]} files - Non-empty file array (use `createMessage` for text-only)
   * @param {Array<{fileName:string,fileType:string,fileSize:number,thumbnailDataUrl:string|null}>} [filesMetadata]
   * @param {object} [opts]
   * @param {(progress:number)=>void} [opts.onProgress] - Called with 0-100 during upload
   * @param {AbortSignal} [opts.signal] - Aborts the XHR
   * @returns {Promise<object>} Saved message
   */
  async uploadMessageWithProgress(threadId, content, files, filesMetadata = [], opts = {}) {
    const { onProgress, signal } = opts;

    if (!files || files.length === 0) {
      return this.createMessage(threadId, content, files, filesMetadata);
    }

    const token = await authService.getToken();
    if (!token) {
      throw new ApiError('No hay sesión activa', 401, 'NO_AUTH_TOKEN');
    }

    const formData = new FormData();
    formData.append('content', content);
    files.forEach((file) => formData.append('files', file));

    if (filesMetadata && filesMetadata.length > 0) {
      const thumbnailsData = filesMetadata.map((meta) => ({
        fileName: meta.fileName,
        thumbnailDataUrl: meta.thumbnailDataUrl,
      }));
      formData.append('clientThumbnails', JSON.stringify(thumbnailsData));
    }

    const url = `${config.apiUrl}/api/threads/${threadId}/messages`;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      // Do NOT set Content-Type; the browser sets multipart boundary.

      xhr.upload.onprogress = (event) => {
        if (!onProgress || !event.lengthComputable) return;
        const pct = Math.min(100, Math.round((event.loaded / event.total) * 100));
        onProgress(pct);
      };

      xhr.onload = () => {
        // Este XHR no pasa por apiClient, así que la señal de consumo hay que
        // publicarla aquí o BillingContext no se entera: un envío CON adjuntos
        // contra el tope devolvía su 402 sin que nadie levantara el aviso (#1299).
        const usageWarning = xhr.getResponseHeader('X-Usage-Warning');
        if (usageWarning) dispatchUsageSignal(parseInt(usageWarning, 10));

        // 204 No Content — unusual for this endpoint but mirror apiClient
        if (xhr.status === 204) {
          resolve(null);
          return;
        }

        let json;
        try {
          json = JSON.parse(xhr.responseText);
        } catch {
          reject(new ApiError(
            xhr.status >= 200 && xhr.status < 300
              ? 'Respuesta inesperada del servidor'
              : `Error del servidor (${xhr.status})`,
            xhr.status,
            'INVALID_RESPONSE'
          ));
          return;
        }

        if (xhr.status < 200 || xhr.status >= 300 || json.success === false) {
          dispatchBillingError(xhr.status, json, 'uploadMessageWithProgress');
          const parts = apiErrorParts(xhr.status, json);
          reject(new ApiError(
            parts.message,
            xhr.status,
            parts.code,
            json.details || null
          ));
          return;
        }

        resolve(json.data);
      };

      xhr.onerror = () => {
        reject(new ApiError('No se puede conectar con el servidor', 0, 'NETWORK_ERROR'));
      };

      xhr.onabort = () => {
        reject(new ApiError('Subida cancelada', 0, 'UPLOAD_ABORTED'));
      };

      if (signal) {
        if (signal.aborted) {
          xhr.abort();
          return;
        }
        signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }

      xhr.send(formData);
    });
  }

  /**
   * Get threads with pagination
   * @param {object} options - Pagination options
   * @param {number} [options.limit] - Max threads to return (default: 30)
   * @param {number} [options.offset] - Offset for pagination (default: 0)
   * @param {string} [options.rolId] - Filter by agent role ID
   * @returns {Promise<{threads: object[], hasMore: boolean, total: number}>}
   */
  async getThreads(options = {}) {
    const limit = options.limit || 30;
    const offset = options.offset || 0;
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    });
    if (options.rolId) queryParams.set('rolId', options.rolId);

    return apiClient.get(`/api/threads?${queryParams}`);
  }

  /**
   * Search threads by title
   * @param {string} searchTerm - Search query
   * @param {object} options - Pagination options
   * @returns {Promise<{threads: object[], hasMore: boolean, total: number}>}
   */
  async searchThreads(searchTerm, options = {}) {
    const limit = options.limit || 50;
    const offset = options.offset || 0;
    const queryParams = new URLSearchParams({
      q: searchTerm,
      limit: limit.toString(),
      offset: offset.toString()
    });

    return apiClient.get(`/api/threads/search?${queryParams}`);
  }

  /**
   * Search threads by title AND message content
   * @param {string} searchTerm - Search query
   * @param {object} options - { limit, offset }
   * @returns {Promise<{threads: object[], hasMore: boolean, total: number}>}
   */
  async searchThreadsWithContent(searchTerm, options = {}) {
    const queryParams = new URLSearchParams({
      q: searchTerm,
      includeContent: 'true',
      limit: (options.limit || 20).toString(),
      offset: (options.offset || 0).toString(),
    });
    return apiClient.get(`/api/threads/search?${queryParams}`);
  }

  /**
   * Get messages for a thread
   * @param {string} threadId - Thread ID
   * @returns {Promise<{messages: object[], agentStatus: object|null}>}
   */
  async getMessages(threadId) {
    return apiClient.get(`/api/threads/${threadId}/messages`);
  }

  /**
   * Delete a thread
   * @param {string} threadId - Thread ID
   * @returns {Promise<null>}
   */
  async deleteThread(threadId) {
    console.log(`[ThreadService] Deleting thread ${threadId}`);
    await apiClient.delete(`/api/threads/${threadId}`);
    console.log('[ThreadService] Thread deleted successfully');
    return true;
  }

  /**
   * Update a thread (e.g., rename)
   * @param {string} threadId - Thread ID
   * @param {object} updates - Fields to update (e.g., { title: 'New Title' })
   * @returns {Promise<object>} Updated thread
   */
  async updateThread(threadId, updates) {
    console.log(`[ThreadService] Updating thread ${threadId}`, updates);
    const result = await apiClient.put(`/api/threads/${threadId}`, updates);
    console.log('[ThreadService] Thread updated successfully:', result);
    return result;
  }

  /**
   * Send an audio message (for transcription)
   * @param {string} threadId - Thread ID
   * @param {Blob} audioBlob - Audio blob to transcribe
   * @returns {Promise<object>} Saved text message (transcribed)
   */
  async sendAudioMessage(threadId, audioBlob) {
    console.log(`[ThreadService] Sending audio for thread ${threadId}`);

    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio_message.webm');

    const result = await apiClient.post(`/api/threads/${threadId}/audio-message`, formData);
    console.log('[ThreadService] Audio processed successfully:', result);
    return result;
  }

  /**
   * Mark a question as answered
   * @param {string} questionId - Question ID (not message ID)
   * @returns {Promise<object>}
   */
  async markQuestionAsAnswered(questionId) {
    console.log(`[ThreadService] Marking question ${questionId} as answered`);
    if (!questionId) {
      throw new Error('Question ID is required');
    }
    return apiClient.put(`/api/threads/questions/${questionId}/mark-answered`);
  }

  /**
   * Refresh signed URLs for a file (when they expire)
   * @param {string} threadId - Thread ID
   * @param {string} fileId - File ID
   * @returns {Promise<{fileUrl: string|null, thumbnailUrl: string|null}>}
   */
  async refreshFileUrl(threadId, fileId) {
    return apiClient.get(`/api/threads/${threadId}/files/${fileId}/refresh-url`);
  }

  /**
   * Request to stop the active turn for a thread (#494).
   * El backend materializa el corte antes de responder: el 200 llega con el
   * estado real ('cancelled'). Sin turno vivo → 409 NO_ACTIVE_TURN (ApiError).
   * @param {string} threadId - Thread ID
   * @returns {Promise<{threadId: string, turnId: string|null, state: string, requestedAt: string}>}
   */
  async stopExecution(threadId) {
    console.log(`[ThreadService] Requesting stop for thread ${threadId}`);
    return apiClient.post(`/api/threads/${threadId}/stop`);
  }

  /**
   * Retry the last user message (resend to N8N)
   * @param {string} threadId - Thread ID
   * @returns {Promise<{retriedMessageId: string, retriedAt: string}>}
   */
  async retryLastMessage(threadId) {
    console.log(`[ThreadService] Retrying last message for thread ${threadId}`);
    return apiClient.post(`/api/threads/${threadId}/retry`);
  }
  async submitFeedback(threadId, messageId, data) {
    return apiClient.post(`/api/threads/${threadId}/messages/${messageId}/feedback`, data);
  }

  async removeFeedback(threadId, messageId) {
    return apiClient.delete(`/api/threads/${threadId}/messages/${messageId}/feedback`);
  }
}

const threadService = new ThreadService();
export default threadService;
